import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
    getSharedAssignments,
    getSharedResponse,
    setSharedResponse,
    type SharedAssignment
} from "../api/assignments";
import {
    exportAssignmentAsA4Image,
    openAssignmentA4Image,
    prefersImageInNewTab
} from "./exportAssignmentImages";
import { AssignmentMapModal } from "./AssignmentMapModal";
import "./shared-assignments-page.css";

/** Datumdelen klipps ut direkt – via Date/UTC hamnar 2026-07-25T00:00+02:00 på föregående dygn. */
const toDateValue = (isoDate?: string): string => isoDate?.slice(0, 10) ?? "";

/** Dagens datum i lokal tid — sv-SE ger redan yyyy-MM-dd. */
const today = new Date().toLocaleDateString("sv-SE");

/** Svarsläge per utdelare — samma tre lägen som admin ser i utdelningslistan. */
type AnswerStatus = "yes" | "no" | "none";

const answerStatus = (canDeliver?: boolean | null): AnswerStatus =>
    canDeliver === true ? "yes" : canDeliver === false ? "no" : "none";

const ANSWER_LABELS: Record<AnswerStatus, string> = {
    yes: "Ja",
    no: "Nej",
    none: "Inget svar"
};

/** Vem man är sparas lokalt, så man slipper välja namn varje gång länken öppnas. */
const STORAGE_KEY = "centerreach.shared.kretsMemberId";

const readStoredMemberId = (): string => {
    try {
        return window.localStorage.getItem(STORAGE_KEY) ?? "";
    } catch {
        return "";
    }
};

const storeMemberId = (value: string): void => {
    try {
        if (value) {
            window.localStorage.setItem(STORAGE_KEY, value);
        } else {
            window.localStorage.removeItem(STORAGE_KEY);
        }
    } catch {
        // Blockerad storage — man får välja namn igen nästa gång.
    }
};

/**
 * Öppen sida bakom en hemlig länk: de flesta utdelare saknar konto, så ingen inloggning krävs.
 * Token i URL:en är hela behörigheten — därför visas varken adresser, telefonnummer eller
 * andras svar här. Man ser sitt eget svar, och uppdragslistan som helhet.
 */
export const SharedAssignmentsPage = (): JSX.Element => {
    const token = new URLSearchParams(window.location.search).get("t") ?? "";

    const { data, isLoading, isError, refetch: refetchShared } = useQuery({
        queryKey: ["assignments", "shared", token],
        queryFn: () => getSharedAssignments(token),
        enabled: !!token,
        staleTime: 60_000,
        retry: 1
    });

    const [memberId, setMemberId] = useState<string>(() => readStoredMemberId());

    // Ett sparat namn som inte finns kvar i kretsen ska inte låsa sidan.
    useEffect(() => {
        if (!data || !memberId) return;
        if (!data.distributors.some((d) => d.id === memberId)) {
            setMemberId("");
            storeMemberId("");
        }
    }, [data, memberId]);

    const responseQuery = useQuery({
        queryKey: ["assignments", "shared", token, "response", memberId],
        queryFn: () => getSharedResponse(token, memberId),
        enabled: !!token && !!memberId && !!data?.round,
        staleTime: 30_000
    });

    const [canDeliver, setCanDeliver] = useState<boolean | null>(null);
    const [homeFrom, setHomeFrom] = useState("");

    // Formuläret speglar det sparade svaret när man byter person.
    useEffect(() => {
        setCanDeliver(responseQuery.data?.canDeliver ?? null);
        setHomeFrom(toDateValue(responseQuery.data?.homeFromDate));
    }, [responseQuery.data]);

    const saveMutation = useMutation({
        mutationFn: (input: { canDeliver: boolean; homeFromDate: string | null }) =>
            setSharedResponse({
                token,
                kretsMemberId: memberId,
                canDeliver: input.canDeliver,
                homeFromDate: input.homeFromDate
            }),
        onSuccess: (saved) => {
            setCanDeliver(saved.canDeliver ?? null);
            setHomeFrom(toDateValue(saved.homeFromDate));
            responseQuery.refetch();
            // Listan över kretsens svar visar även ens eget – den måste hämtas om.
            refetchShared();
            toast.success("Tack, ditt svar är sparat.");
        },
        onError: (error: unknown) => {
            // Skilj "nådde inte servern" från ett avvisat svar — utdelarna sitter ofta på mobilen.
            const reachedServer =
                !!error && typeof error === "object" && "response" in error &&
                !!(error as { response?: unknown }).response;
            toast.error(
                reachedServer
                    ? "Kunde inte spara svaret. Ladda om sidan och försök igen."
                    : "Ingen kontakt med servern. Kontrollera din uppkoppling och försök igen."
            );
        }
    });

    // Sparat läge att jämföra mot, så knappen kan visa om något är osparat.
    const savedCanDeliver = responseQuery.data?.canDeliver ?? null;
    const savedHomeFrom = toDateValue(responseQuery.data?.homeFromDate);
    const isDirty = canDeliver !== savedCanDeliver || homeFrom !== savedHomeFrom;

    const handleSave = () => {
        if (canDeliver === null) return;
        saveMutation.mutate({
            canDeliver,
            homeFromDate: canDeliver ? homeFrom || null : null
        });
    };

    const [exportingId, setExportingId] = useState<string | null>(null);
    // Två lägen: titta på kartan (zoombar, bra i mobilen) eller spara ned den som bild.
    const [mapAssignment, setMapAssignment] = useState<SharedAssignment | null>(null);

    const handleDownloadMap = async (assignment: SharedAssignment) => {
        setExportingId(assignment.id);
        try {
            // Utan adress/telefon — den öppna sidan har inte de uppgifterna.
            // På telefonen öppnas bilden i en ny flik i stället för att laddas ned.
            const showImage = prefersImageInNewTab() ? openAssignmentA4Image : exportAssignmentAsA4Image;
            await showImage(assignment, { includeDistributorDetails: false });
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Kunde inte hämta kartan.");
        } finally {
            setExportingId(null);
        }
    };

    const selectedName = useMemo(
        () => data?.distributors.find((d) => d.id === memberId)?.name ?? "",
        [data?.distributors, memberId]
    );

    const roundName = data?.round?.name ?? "";
    const isSaving = saveMutation.isPending;
    const hasAnswer = canDeliver !== null;

    return (
        <div className="shared-assignments-page">
            <header className="shared-assignments-page__header">
                <span className="shared-assignments-page__logo">CenterReach</span>
            </header>

            <main className="shared-assignments-page__content">
                <h1 className="shared-assignments-page__title">
                    Utdelning{data?.municipalityName ? ` – ${data.municipalityName}` : ""}
                </h1>

                {!token && (
                    <p className="shared-assignments-page__hint">
                        Länken är ofullständig. Be den som skickade den om en ny länk.
                    </p>
                )}
                {token && isLoading && <p className="shared-assignments-page__hint">Laddar…</p>}
                {token && isError && (
                    <p role="alert" className="shared-assignments-page__error">
                        Länken fungerar inte längre. Be den som skickade den om en ny.
                    </p>
                )}

                {data && !data.round && (
                    <p className="shared-assignments-page__hint">
                        Ingen utdelning är öppen för svar just nu.
                    </p>
                )}

                {data?.round && (
                    <section className="shared-answer">
                        <h2 className="shared-answer__heading">{roundName}</h2>

                        <label className="shared-answer__field">
                            Vem är du?
                            <select
                                value={memberId}
                                onChange={(event) => {
                                    setMemberId(event.target.value);
                                    storeMemberId(event.target.value);
                                }}
                            >
                                <option value="">Välj ditt namn</option>
                                {data.distributors.map((distributor) => (
                                    <option key={distributor.id} value={distributor.id}>
                                        {distributor.name}
                                    </option>
                                ))}
                            </select>
                        </label>

                        {!memberId && (
                            <p className="shared-assignments-page__hint">
                                Välj ditt namn för att svara. Hittar du inte namnet, hör av dig till din
                                kretsansvarige.
                            </p>
                        )}

                        {memberId && (
                            <>
                                <p className="shared-answer__question">
                                    Kan du dela ut <strong>{roundName}</strong>?
                                </p>
                                <div className="shared-answer__choices">
                                    <button
                                        type="button"
                                        className={`shared-answer__choice${canDeliver === true ? " shared-answer__choice--active" : ""}`}
                                        disabled={isSaving}
                                        onClick={() => setCanDeliver(true)}
                                    >
                                        Ja
                                    </button>
                                    <button
                                        type="button"
                                        className={`shared-answer__choice${canDeliver === false ? " shared-answer__choice--active" : ""}`}
                                        disabled={isSaving}
                                        onClick={() => {
                                            setCanDeliver(false);
                                            setHomeFrom("");
                                        }}
                                    >
                                        Nej
                                    </button>
                                </div>

                                {canDeliver === true && (
                                    <div className="shared-answer__field">
                                        Från vilket datum är du hemma och kan ta emot material?
                                        <div className="shared-answer__date-row">
                                            <input
                                                type="date"
                                                value={homeFrom}
                                                disabled={isSaving}
                                                aria-label="Hemma från"
                                                onChange={(event) => setHomeFrom(event.target.value)}
                                            />
                                            {/* Vanligaste fallet: hemma redan nu — slipp datumväljaren helt. */}
                                            <button
                                                type="button"
                                                className="shared-answer__today-button"
                                                disabled={isSaving || homeFrom === today}
                                                onClick={() => setHomeFrom(today)}
                                            >
                                                Jag är hemma redan nu
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <button
                                    type="button"
                                    className="shared-answer__save"
                                    disabled={canDeliver === null || isSaving || !isDirty}
                                    onClick={handleSave}
                                >
                                    {isSaving ? "Sparar…" : isDirty ? "Spara svar" : "Sparat ✓"}
                                </button>

                                {isDirty && canDeliver !== null && !isSaving && (
                                    <p className="shared-answer__unsaved">
                                        Du har ändringar som inte är sparade.
                                    </p>
                                )}
                                {!isDirty && hasAnswer && responseQuery.data?.respondedAt && !isSaving && (
                                    <p className="shared-answer__receipt">
                                        Ditt svar är sparat: <strong>{canDeliver ? "Ja" : "Nej"}</strong>
                                        {canDeliver && homeFrom ? `, hemma från ${homeFrom}` : ""}. Du kan ändra
                                        det här när som helst.
                                    </p>
                                )}
                                {canDeliver === true && !homeFrom && (
                                    <p className="shared-assignments-page__hint">
                                        Välj datum eller “Jag är hemma redan nu” innan du sparar.
                                    </p>
                                )}
                            </>
                        )}
                    </section>
                )}

                {/* Vem mer som delar ut den här omgången – utdelarna vill se helheten. */}
                {data?.round && data.distributors.length > 0 && (
                    <section className="shared-assignments-page__list">
                        <h2 className="shared-assignments-page__subtitle">Utdelare</h2>
                        <p className="shared-assignments-page__summary">
                            {data.distributors.filter((d) => d.canDeliver === true).length} ja ·{" "}
                            {data.distributors.filter((d) => d.canDeliver === false).length} nej ·{" "}
                            {data.distributors.filter((d) => d.canDeliver == null).length} inget svar
                        </p>

                        <table className="shared-assignments-table">
                            <thead>
                                <tr>
                                    <th>Namn</th>
                                    <th>Kan dela ut</th>
                                    <th>Hemma från</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.distributors.map((distributor) => {
                                    const status = answerStatus(distributor.canDeliver);
                                    const isMe = distributor.id === memberId;
                                    return (
                                        <tr
                                            key={distributor.id}
                                            className={isMe ? "shared-assignments-table__row--mine" : ""}
                                        >
                                            <td>
                                                <span className="shared-assignments-table__name">
                                                    {distributor.name}
                                                    {isMe && (
                                                        <span className="shared-assignments-table__badge">du</span>
                                                    )}
                                                </span>
                                            </td>
                                            <td>
                                                <span
                                                    className={`shared-assignments-table__chip shared-assignments-table__chip--${status}`}
                                                >
                                                    {ANSWER_LABELS[status]}
                                                </span>
                                            </td>
                                            <td className="shared-assignments-table__date">
                                                {toDateValue(distributor.homeFromDate) || "–"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </section>
                )}

                {!!data?.items.length && (
                    <section className="shared-assignments-page__list">
                        <h2 className="shared-assignments-page__subtitle">
                            Uppdrag i {data.municipalityName}
                        </h2>

                        <table className="shared-assignments-table">
                            <thead>
                                <tr>
                                    <th>Namn</th>
                                    <th className="shared-assignments-table__num">Antal</th>
                                    <th className="shared-assignments-table__check">Fått material</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((assignment) => {
                                    const isMine = !!memberId && assignment.kretsMemberId === memberId;
                                    return (
                                        <tr
                                            key={assignment.id}
                                            className={isMine ? "shared-assignments-table__row--mine" : ""}
                                        >
                                            <td>
                                                <span className="shared-assignments-table__name">
                                                    {assignment.name}
                                                    {isMine && (
                                                        <span className="shared-assignments-table__badge">ditt</span>
                                                    )}
                                                </span>
                                                {assignment.distributorName && (
                                                    <span className="shared-assignments-table__distributor">
                                                        {assignment.distributorName}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="shared-assignments-table__num">
                                                {assignment.targetAddressCount ?? "–"}
                                            </td>
                                            <td className="shared-assignments-table__check">
                                                <input
                                                    type="checkbox"
                                                    checked={assignment.materialReceived}
                                                    disabled
                                                    aria-label={`Fått material: ${assignment.name}`}
                                                />
                                            </td>
                                            <td>
                                                {assignment.area ? (
                                                    <div className="shared-assignments-table__map-actions">
                                                        <button
                                                            type="button"
                                                            className="shared-assignments-table__map-button"
                                                            onClick={() => setMapAssignment(assignment)}
                                                        >
                                                            Visa karta
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="shared-assignments-table__map-button shared-assignments-table__map-button--secondary"
                                                            disabled={exportingId === assignment.id}
                                                            onClick={() => handleDownloadMap(assignment)}
                                                            title="Kartan som bild"
                                                        >
                                                            {exportingId === assignment.id
                                                                ? "Hämtar…"
                                                                : prefersImageInNewTab()
                                                                  ? "Visa bild"
                                                                  : "Spara bild"}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span className="shared-assignments-table__no-map">–</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </section>
                )}

                {selectedName && (
                    <p className="shared-assignments-page__footer-note">
                        Ditt svar sparas på <strong>{selectedName}</strong>. Är det inte du, byt namn i listan
                        ovan.
                    </p>
                )}
            </main>

            {mapAssignment && (
                <AssignmentMapModal
                    assignment={mapAssignment}
                    onClose={() => setMapAssignment(null)}
                    onDownload={() => handleDownloadMap(mapAssignment)}
                    isDownloading={exportingId === mapAssignment.id}
                />
            )}
        </div>
    );
};

export default SharedAssignmentsPage;
